// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import styled from 'styled-components';

import { type MenuAddKeyType, type MenuSetKeyType, type MenuResetType } from '../../actions/menu';
import { Star } from '../../assets/svg';
import { DetailedClock } from '../../assets/svg/common';
import { SPORTS_MENU_LIST_ITEM_BORDER_RADIUS, SPORTS_MENU_LIST_ITEM_HEIGHT } from '../../constants/ui/sportsMenu';
import { SportsMenuContext } from '../../contexts/SportsMenuContext';
import type { CustomRouterHistory } from '../../createHistory';
import { getAppDOMElement } from '../../DOMElements';
import useRouter from '../../hooks/useRouter';
import useSportsMenuScrollContext from '../../hooks/useSportsMenuScrollContext';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { SportsMenuTitleLabel } from '../../styled-components/shared';
import { MenuKeyTypes, type MenuKeyType } from '../../types/common';
import { scrollToTop } from '../../utils';
import Routes from '../../utils/routes';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import Counter from '../Counter';
import ExpandArrow from '../ExpandArrow';
import ListItem, { ListWrapper } from '../ListItem';
import SportIcon from '../SportIcon';
import SportsMenuSportsList from '../SportsMenuSportsList';
import SportsMenuTopCompetitions from '../SportsMenuTopCompetitions';
import TopActionLine from '../TopActionLine';

const SportsMenuWrapper = styled.div`
  height: 100%;
  color: white;
`;

const StyledTopListWrapper = styled(ListWrapper)`
  background-color: ${theme.SportsMenuListItem.backgroundColor};
  border-radius: ${SPORTS_MENU_LIST_ITEM_BORDER_RADIUS}px;
  margin-top: 1.4em;
`;

const StyledArrow = styled(ExpandArrow)`
  position: absolute;
  right: 0.7em;
  height: 1em;
  width: 1em;
  transition: transform 150ms ease-in-out;
  transform: rotate(90deg);

  & .sports-arrow-inner-group {
    stroke: ${theme.SportsMenuListItem.arrowColor};
  }
`;

const StyledListItem = styled(ListItem)`
  background-image: none;
  padding: 0 0.5em;
  height: ${SPORTS_MENU_LIST_ITEM_HEIGHT}em;
  border-top: none;
  letter-spacing: normal;

  &:active {
    background-color: ${theme.SportsMenuListItem.active.backgroundColor};
  }
`;

const StarIcon = styled(Star)`
  width: 1.4em;
  height: 1.4em;
  margin: 0 1em 0 0.5em;
`;

const StyledCounter = styled(Counter)`
  width: 1.6em;
  height: 1.6em;
  font-weight: bold;
`;

const ClockIcon = styled(DetailedClock)`
  width: 2em;
  height: 2em;
  margin: 0 0.7em 0 0.2em;
`;

const StyledTopCompetitions = styled(SportsMenuTopCompetitions)`
  margin-top: 1.4em;
`;

const BtnContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const BtnLabel = styled.div`
  display: flex;
  font-size: 1em;
  flex-direction: row;
  align-items: center;
  flex: 1;
  height: 100%;

  & > div {
    margin-left: 1em;
  }

  border-top: ${({ $separator }) => ($separator ? `1px solid ${theme.SportsMenuListItem.separatorColor}` : undefined)};
`;

const SportsWrapper = styled.div`
  margin-top: 1.4em;
`;

const StyledTitleLabel = styled(SportsMenuTitleLabel)`
  padding-left: 0.5em;
`;

type PropsType = {
  clearCalendarPreferences: () => void;
  clearFavoritePreferences: () => void;
  sportName: string;
  history: CustomRouterHistory;
  type: MenuKeyType;
  config: {
    header: string;
    topCompetitions: Array<{
      flagId?: string;
      label: string;
      sportId: number;
      onClick: () => void;
    }>;
    content: Array<unknown>;
  };
  connected: boolean;
  menu: unknown;
  nbFavorites: number;
  actions: {
    menuAddKey: MenuAddKeyType;
    menuSetKey: MenuSetKeyType;
    menuReset: MenuResetType;
  };
};

const MobileSportsMenu = ({
  config,
  sportName,
  nbFavorites,
  type = MenuKeyTypes.DEFAULT,
  menu,
  actions,
  connected = false,
  clearCalendarPreferences,
  clearFavoritePreferences,
}: PropsType) => {
  const {
    history,
    params: { sportId },
  } = useRouter();
  const { location, previousPathname } = history;

  const scrollElement = getAppDOMElement();
  const { scrollPositions, setScrollPositions } = useSportsMenuScrollContext();

  const showFavoriteButton = useMemo(() => connected && !sportId, [connected, sportId]);

  const navigateTo = useCallback((path) => () => history.push(path), [history]);

  const onClickFavoriteButton = useCallback(() => {
    navigateTo(RoutesRedirectionFactory.getFavoritesRouteConfig())();
    clearFavoritePreferences();
  }, [navigateTo, clearFavoritePreferences]);

  const onClickCalendarButton = useCallback(() => {
    navigateTo(RoutesRedirectionFactory.getCalendarRouteConfig())();
    clearCalendarPreferences();
  }, [navigateTo, clearCalendarPreferences]);

  useLayoutEffect(
    () => {
      if (!Routes.isCatalog(history.getPreviousPath())) {
        setScrollPositions({});
        actions.menuReset();
      }
    },
    // Call useEffect uniquement quand actions est défini
    // Ingore pour history et setScrollPositions
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions],
  );

  useEffect(
    () => {
      if (scrollPositions[location.pathname] !== undefined) {
        scrollElement.scrollTop = scrollPositions[location.pathname];
      } else if (location.pathname !== previousPathname) {
        scrollToTop();
      }
    },
    // Call useEffect uniquement quand le location.pathname et previousPathname changent
    // Ingore pour scrollElement.scrollTop et scrollPositions
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, previousPathname],
  );

  const openKeys = useMemo(() => menu[type]?.openKeys, [menu, type]);

  const forwardedState = useMemo(
    () => ({
      openKeys: openKeys ?? [],
    }),
    [openKeys],
  );

  const onExpandClick = useCallback(
    (key) => {
      actions.menuAddKey(type, key);
    },
    [actions, type],
  );

  const content = useMemo(
    () => (
      <SportsMenuWrapper>
        {sportId && sportName ? (
          <TopActionLine
            ctaLabel={__('See all')}
            icon={<SportIcon sportId={sportId} />}
            label={sportName}
            onButtonClick={navigateTo(RoutesRedirectionFactory.getCatalogRouteConfig(sportId))}
            showArrow
            showButton
          />
        ) : null}
        {showFavoriteButton || !sportId ? (
          <StyledTopListWrapper>
            {showFavoriteButton ? (
              <StyledListItem onClick={onClickFavoriteButton}>
                <BtnContent>
                  <StarIcon />
                  <BtnLabel>
                    {__('My favourites')} <StyledCounter counter={nbFavorites} />
                  </BtnLabel>
                  <StyledArrow />
                </BtnContent>
              </StyledListItem>
            ) : null}

            {!sportId ? (
              <StyledListItem onClick={onClickCalendarButton}>
                <BtnContent>
                  <ClockIcon />
                  <BtnLabel $separator={showFavoriteButton}>{__('Upcoming matches')}</BtnLabel>
                  <StyledArrow />
                </BtnContent>
              </StyledListItem>
            ) : null}
          </StyledTopListWrapper>
        ) : null}
        {config?.topCompetitions?.length ? (
          <StyledTopCompetitions items={config.topCompetitions} showSportIcon={!sportId} />
        ) : null}
        <SportsWrapper>
          <StyledTitleLabel>{config.header}</StyledTitleLabel>
          <SportsMenuSportsList content={config.content} onExpandClick={onExpandClick} />
        </SportsWrapper>
      </SportsMenuWrapper>
    ),
    [
      config.content,
      config.header,
      config.topCompetitions,
      navigateTo,
      nbFavorites,
      onClickCalendarButton,
      onClickFavoriteButton,
      onExpandClick,
      showFavoriteButton,
      sportId,
      sportName,
    ],
  );

  return <SportsMenuContext.Provider value={forwardedState}>{content}</SportsMenuContext.Provider>;
};

export default MobileSportsMenu;
